/**
 * API Client for ActuallyHelpfulAssistant Backend
 * 
 * Handles all API requests with SSO token authentication.
 * Requirements: 8.1, 8.2
 */

import { getAuthToken, getLocationId } from '../auth/session';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
}

/**
 * Make an authenticated API request
 * Attaches SSO token to Authorization header
 */
async function apiRequest<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const token = getAuthToken();
  const locationId = getLocationId();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  // Attach SSO token for authentication (Requirement 8.2)
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Also send locationId header as fallback
  if (locationId) {
    headers['X-Location-Id'] = locationId;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Types matching backend
export interface SearchResult {
  chunkId: string;
  text: string;
  score: number;
  vectorScore: number;
  keywordScore: number;
  metadata: {
    locationId: string;
    contactId: string;
    callId: string;
    callDate: string;
    chunkIndex: number;
  };
}

export interface SearchResponse {
  results: SearchResult[];
  query: string;
  totalResults: number;
  mode?: string;
  degraded?: boolean;
}

export interface GlobalSearchResultItem {
  contactId: string;
  contactName: string;
  callId: string;
  callDate: string;
  snippet: string;
  score: number;
}

export interface ContactGroup {
  contactId: string;
  contactName: string;
  results: Array<{
    callId: string;
    callDate: string;
    snippet: string;
    score: number;
  }>;
}

export interface GlobalSearchResponse {
  results: GlobalSearchResultItem[];
  groupedResults: ContactGroup[];
  query: string;
  totalResults: number;
}

export interface SourceReference {
  callId: string;
  callDate: string;
  snippet: string;
}

export interface ChatResponse {
  messageId: string;
  response: string;
  sources: SourceReference[];
  conversationId: string;
}

export interface UsageResponse {
  locationId: string;
  billingPeriod: string;
  callsProcessed: number;
  chatMessages: number;
  plan: {
    tier: string;
    callsLimit: number;
    messagesLimit: number;
  };
}

export interface Transcript {
  transcriptId: string;
  locationId: string;
  contactId: string;
  callId: string;
  fullText: string;
  utterances: Array<{
    speaker: string;
    text: string;
    start: number;
    end: number;
  }>;
  durationMs: number;
  callDate: number;
}

export interface Summary {
  summaryId: string;
  transcriptId: string;
  quickSummary: string;
  keyPoints: string[];
  actionItems: Array<{ task: string; owner?: string }>;
  sentiment: 'positive' | 'neutral' | 'negative' | 'mixed';
  importantQuotes: Array<{ text: string; speaker: string }>;
  followUpRequired: boolean;
  followUpReason?: string;
}

// API functions
export const api = {
  /**
   * Search within a contact's calls
   */
  search: (query: string, contactId?: string, limit?: number): Promise<SearchResponse> =>
    apiRequest('/api/search', {
      method: 'POST',
      body: { query, contactId, limit },
    }),

  /**
   * Global search across all contacts
   */
  globalSearch: (query: string, limit?: number): Promise<GlobalSearchResponse> =>
    apiRequest('/api/search/global', {
      method: 'POST',
      body: { query, limit },
    }),

  /**
   * Send a chat message for a contact
   */
  chat: (contactId: string, message: string, conversationId?: string): Promise<ChatResponse> =>
    apiRequest('/api/chat', {
      method: 'POST',
      body: { contactId, message, conversationId },
    }),

  /**
   * Get usage statistics
   */
  getUsage: (): Promise<UsageResponse> =>
    apiRequest('/api/admin/usage'),

  /**
   * Get recent calls (transcripts with summaries)
   */
  getRecentCalls: async (limit = 10): Promise<Array<{ transcript: Transcript; summary?: Summary }>> => {
    const response = await apiRequest<{ calls: Array<{ transcript: Transcript; summary?: Summary }> }>(
      `/api/calls?limit=${limit}`
    );
    return response.calls;
  },

  /**
   * Get transcripts for a contact
   */
  getContactTranscripts: async (contactId: string): Promise<Transcript[]> => {
    const response = await apiRequest<{ transcripts: Transcript[] }>(
      `/api/contacts/${contactId}/transcripts`
    );
    return response.transcripts;
  },

  /**
   * Get summary for a transcript
   */
  getSummary: async (transcriptId: string): Promise<Summary | null> => {
    const response = await apiRequest<{ summary: Summary | null }>(
      `/api/transcripts/${transcriptId}/summary`
    );
    return response.summary;
  },
};
